import round from 'lodash/round';

import MoleculeBase from './MoleculeBase';

import { getInstance } from '../initialiser';

import { MoleculeError } from '../../utils';

import { Mol } from '../../types';

type GetMorganFP = {
  radius: number;
  nBits: number;
  useChirality?: boolean;
  useBondTypes?: boolean;
  onlyNonzeroInvariants?: boolean;
  useFeatures?: boolean;
};

export default class MoleculeFull extends MoleculeBase {
  _inchi: string | null;
  _inchiKey: string | null;
  _1dDescriptors: number[];
  _fingerprints: Map<number, number[]>;

  constructor(mol: Mol) {
    super(mol);
    this._inchi = null;
    this._inchiKey = null;
    this._1dDescriptors = [];
    this._fingerprints = new Map();
  }

  static fromSmarts(smarts: string): MoleculeFull {
    const {
      exports: { get_qmol },
    } = getInstance();

    return new MoleculeFull(get_qmol(smarts));
  }

  static fromSmiles(smiles: string): MoleculeFull {
    const {
      exports: { get_mol },
    } = getInstance();

    return new MoleculeFull(get_mol(smiles));
  }

  static getInchikeyForInchi(inchi: string): string {
    const {
      exports: { get_inchikey_for_inchi },
    } = getInstance();

    return get_inchikey_for_inchi(inchi);
  }

  get1DDescriptors(): number[] {
    if (this._1dDescriptors.length === 0) {
      const slogP_VSA = this._mol.slog_p_vsa();

      const slogP_VSA_values = Array.from({ length: 12 }, (_v, i) => {
        const v = slogP_VSA.get(i);
        if (v == null) throw new MoleculeError(`slogP_VSA null at index ${i}`);
        return round(v, 8);
      });

      this._1dDescriptors = [
        round(this._mol.tpsa(), 2),
        ...slogP_VSA_values,
        round(this._mol.clog_p(), 4),
        round(this._mol.mr(), 4),
        this._mol.num_rotatable_bonds(),
        this._mol.num_hba(),
        this._mol.num_hbd(),
        this._mol.num_aromatic_rings(),
        this._mol.num_saturated_rings(),
        this._mol.num_aliphatic_rings(),
        this._mol.num_aromatic_heterocycles(),
        this._mol.num_saturated_heterocycles(),
        this._mol.num_aliphatic_heterocycles(),
        this._mol.num_aromatic_carbocycles(),
        this._mol.num_saturated_carbocycles(),
        this._mol.num_aliphatic_carbocycles(),
      ];

      //slogP_VSA.delete();
    }

    return this._1dDescriptors;
  }

  getMorganFP(opts: GetMorganFP): number[] {
    const {
      radius,
      nBits,
      useChirality = false,
      useBondTypes = true,
      onlyNonzeroInvariants = false,
      useFeatures = false,
    } = opts;

    const key =
      (nBits << 16) |
      (radius << 4) |
      (Number(useChirality) << 3) |
      (Number(useBondTypes) << 2) |
      (Number(onlyNonzeroInvariants) << 1) |
      Number(useFeatures);

    let fingerprint = this._fingerprints.get(key);
    if (fingerprint == null) {
      const str = this._mol.get_morgan_fp(
        radius,
        nBits,
        useChirality,
        useBondTypes,
        onlyNonzeroInvariants,
        useFeatures
      );

      fingerprint = str.split('').map(Number);
      this._fingerprints.set(key, fingerprint);
    }

    return fingerprint;
  }

  getInchi(): string {
    if (this._inchi == null) {
      this._inchi = this._mol.get_inchi();
    }

    return this._inchi;
  }

  getInchiKey(): string {
    if (this._inchiKey == null) {
      this._inchiKey = MoleculeFull.getInchikeyForInchi(this.getInchi());
    }

    return this._inchiKey;
  }

  hasSubstructMatchStr(smilesRef: string): boolean {
    return this._mol.has_substruct_match_str(smilesRef);
  }
}
